import GeneralHelper from '../../00_utilities/helpers/GeneralHelper';

class Hero {
    constructor(elem) {
        this.elem = elem;
        this.initialTopValue = 0;
        this.initialScrollTop = window.pageYOffset;
        this.pageHeaderHeight = GeneralHelper.getPageHeaderHeight();
        this.boundWindowScrollListener = this.toggleScrollHint.bind(this);
        this.boundWindowScrollListenerSplitScreen = this.setSplitScreenPosition.bind(this);
        this.scrollHint = this.elem.querySelector('.hero__scroll-hint');
        this.firstImage = this.elem.querySelector('.hero__image');
        this.button = this.elem.querySelector('.hero__button');
        this.buttonHiddenClass = 'hero__button--hidden';
    }

    init() {
        this.elem.setAttribute('data-initialized', true);

        if (this.elem.classList.contains('hero--split-screen')) {
            this.initSplitScreenAnimation();
        }

        if (this.elem.classList.contains('hero--image-blend')) {
            this.initImageBlend();
        }

        this.initItemVideoListener();
        this.initTracking();
        this.initScrollHint();
    }

    initItemVideoListener() {
        const heroItems = this.elem.querySelectorAll('.hero__item');

        if (heroItems) {
            const options = { attributes: true, childList: true, subtree: true };

            const observer = new MutationObserver((mutationList) => {
                for (const mutation of mutationList) {
                    if (mutation.type === 'childList') {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeName && node.nodeName.toLowerCase() === 'video') {
                                node.addEventListener('play', () => {
                                    node.closest('.hero__item').classList.add('hero__item--video-playing');
                                }, { once: true });
                            }
                        });

                        observer.disconnect();
                    }
                }
            });

            for (const item of heroItems) {
                observer.observe(item, options);
            }
        }
    }

    initScrollHint() {
        if (this.scrollHint) {
            const options = {
                rootMargin: `${this.pageHeaderHeight * -1}px 0px 0px 0px`,
                threshold: [0],
            };

            const observer = new IntersectionObserver((entries) => {
                const isIntersecting = typeof entries[0].isIntersecting === 'boolean'
                    ? entries[0].isIntersecting : entries[0].intersectionRatio > 0;

                if (isIntersecting) {
                    window.addEventListener('scroll', this.boundWindowScrollListener);

                    return;
                }
                window.removeEventListener('scroll', this.boundWindowScrollListener);
            }, options);

            observer.observe(this.elem);
        }
    }

    toggleScrollHint() {
        const scrollHintHiddenClass = 'hero__scroll-hint--hidden';
        const scrollTop = window.pageYOffset;
        const scrollingDown = scrollTop > this.initialScrollTop;

        if (scrollingDown) {
            this.scrollHint.classList.add(scrollHintHiddenClass);
            return;
        }

        this.scrollHint.classList.remove(scrollHintHiddenClass);
    }

    initSplitScreenAnimation() {
        const images = this.elem.querySelectorAll('.hero__image');
        if (images.length === 2) {
            this.initialTopValue = this.getInitialTopValue();

            this.setSplitScreenPosition();
            this.initSplitScreenObserver(images);
        }
    }

    initSplitScreenObserver(images) {
        const imageHiddenClass = 'hero__image--hidden';
        const options = {
            rootMargin: `${this.pageHeaderHeight * -1}px 0px 0px 0px`,
            threshold: [0, 0.25],
        };

        const observer = new IntersectionObserver((entries) => {
            const isIntersecting = typeof entries[0].isIntersecting === 'boolean'
                ? entries[0].isIntersecting : entries[0].intersectionRatio > 0;

            if (isIntersecting) {
                images[1].classList.remove(imageHiddenClass);
                window.addEventListener('scroll', this.boundWindowScrollListenerSplitScreen);

                if (this.button && entries[0].intersectionRatio < 0.25) {
                    this.button.classList.remove(this.buttonHiddenClass);

                    return;
                }
                this.button.classList.add(this.buttonHiddenClass);

                return;
            }

            window.removeEventListener('scroll', this.boundWindowScrollListenerSplitScreen);
            images[1].classList.add(imageHiddenClass);
        }, options);

        observer.observe(images[0]);
    }

    getInitialTopValue() {
        const mainContentTopValue = document.getElementById('content').offsetTop;
        return this.elem.offsetTop + mainContentTopValue;
    }

    setSplitScreenPosition() {
        const imageScrollValue = this.firstImage.getBoundingClientRect().top;
        const inverseScrollValue = (-2 * imageScrollValue) + (2 * this.initialTopValue);

        if (imageScrollValue >= -this.firstImage.scrollHeight && imageScrollValue <= this.pageHeaderHeight) {
            this.elem.style.setProperty('--hero-image-block-start', `${inverseScrollValue}px`);
        }
    }

    initImageBlend() {
        this.scrollIndicators = this.elem.querySelectorAll('.hero__scroll-indicator');
        this.items = this.elem.querySelectorAll('.hero__bg-color-layer');

        this.previousY = this.initArray([]);
        this.previousRatio = this.initArray([]);

        const maxIntersectionRatio = this.getMaxIntersectionRatio(this.scrollIndicators[0]);
        const options = {
            threshold: this.buildThresholdList(10, maxIntersectionRatio),
        };

        const observer = new IntersectionObserver(
            this.handleIntersect.bind(this),
            options,
        );

        for (let i = 0; i < this.scrollIndicators.length; i += 1) {
            if (i !== 0) {
                observer.observe(this.scrollIndicators[i]);
            }
        }
    }

    buildThresholdList(numSteps, maxValue) {
        const thresholds = [0];

        for (let i = 1.0; i <= numSteps; i += 1) {
            const ratio = i / numSteps;
            if (ratio <= maxValue) {
                thresholds.push(ratio);
            }
        }
        return thresholds;
    }

    initArray(array) {
        for (let i = 1; i <= this.scrollIndicators.length; i += 1) {
            array[i] = 0;
        }
        return array;
    }

    getMaxIntersectionRatio(item) {
        const computedStyle = window.getComputedStyle(item);
        const height = parseInt(computedStyle.getPropertyValue('height'), 10);
        let maxIntersectionRatio = 1;

        if (window.innerHeight < height) {
            maxIntersectionRatio = (window.innerHeight / height).toFixed(2);
        }

        return maxIntersectionRatio;
    }

    handleIntersect(entries) {
        if (this.items) {
            entries.forEach((entry) => {
                const parent = entry.target.parentNode;
                const currentIndex = Array.prototype.indexOf.call(
                    parent.children,
                    entry.target,
                );
                const currentY = entry.boundingClientRect.y;
                const currentRatio = entry.intersectionRatio;

                this.items[currentIndex].style.setProperty('--active', `${currentRatio}`);
                this.previousY[currentIndex] = currentY;
                this.previousRatio[currentIndex] = currentRatio;

                if (this.button) {
                    if (entry.target === this.scrollIndicators[this.scrollIndicators.length - 1]
                        && entry.intersectionRatio > 0.25) {
                        this.button.classList.remove(this.buttonHiddenClass);

                        return;
                    }
                    this.button.classList.add(this.buttonHiddenClass);
                }
            });
        }
    }

    initTracking() {
        const buttons = this.elem.querySelectorAll('.button');
        buttons.forEach((button) => {
            button.addEventListener('click', () => {
                const container = button.closest('.hero__content-container');

                if (container) {
                    const headline = container.querySelector('.hero__header').innerHTML;

                    const dataLayerObject = {
                        event: 'HeroPromotion',
                        title: headline,
                    };

                    window.dataLayer.push(dataLayerObject);
                }
            });
        });
    }
}

export default Hero;
